import { hyp } from '../../core/exactMath';
import type { GridPoint } from '../../core/types';
import type { WorldMap } from '../../world/WorldMap';
import type { Building, BuildingStore } from '../buildings/BuildingStore';
import { buildingDef, statsAt } from '../buildings/definitions';
import { footprintTiles } from '../buildings/footprint';
import { Projectiles, type Projectile, type TargetRef } from '../combat/Projectiles';
import { COMBAT, computeDamage, hostile, type DamageType } from '../combat/rules';
import type { BonusKey } from '../research/Research';
import type { OwnerId } from '../ids';
import { findPath, nearestOpenTiles } from '../pathing/AStar';
import { FlowFields } from '../pathing/FlowField';
import { unitDef, type Stance, type UnitDefinition, type UnitTypeId } from './unitDefs';

export type { TargetRef } from '../combat/Projectiles';

export type UnitOrder =
  | { readonly kind: 'idle' }
  | { readonly kind: 'move'; readonly target: GridPoint }
  /** Walk to `target`, fighting any enemy met on the way (Phase 6). */
  | { readonly kind: 'attackMove'; readonly target: GridPoint }
  /** `auto` = chosen by the unit itself (sight / return fire), as opposed to ordered by the player. */
  | { readonly kind: 'attack'; readonly target: TargetRef; readonly auto?: boolean }
  /** Engineers (Phase 8): walk to one of your buildings and restore its health, paying scrap. */
  | { readonly kind: 'repair'; readonly buildingId: number; readonly auto?: boolean };

/** Read-only, serialisable view of a unit. Positions are fractional GRID coordinates (tile centres are integers). */
export interface Unit {
  readonly id: number;
  readonly type: UnitTypeId;
  readonly ownerId: OwnerId;
  readonly x: number;
  readonly y: number;
  readonly health: number;
  readonly maxHealth: number;
  readonly order: UnitOrder;
  readonly stance: Stance;
  /** Remaining waypoints (tile centres). */
  readonly path: readonly GridPoint[];
  readonly cooldown: number;
}

interface MutableUnit {
  id: number;
  type: UnitTypeId;
  ownerId: OwnerId;
  x: number;
  y: number;
  health: number;
  maxHealth: number;
  order: UnitOrder;
  stance: Stance;
  path: GridPoint[];
  cooldown: number;
  /** Seconds until a chasing unit may re-plan its path. */
  repath: number;
  /** Where an automatic engagement began (leash centre; aggressive units walk back here if they give up). */
  anchor: GridPoint | null;
  /** Attack-move destination to carry on to once the current fight is over. */
  resume: GridPoint | null;
  /** Last attacker: a unit id, or −buildingId for a turret (answered on the next step if the unit is free to respond). */
  threat: number | null;
  /** A target it could not reach: not picked again for `skipFor` seconds. */
  skip: TargetRef | null;
  skipFor: number;
  /** Fractional health restored but not yet applied (repairs apply whole points). */
  repairAcc: number;
  /** A move / attack-move route still to be planned (the per-tick planning budget ran out). */
  pendingPlan: GridPoint | null;
}

/** Most routes planned during one simulation step (D80). Units over the budget plan on a later tick (id order). */
const PLAN_BUDGET_PER_TICK = 16;
/** Waypoints an infected takes from a flow field before asking again (D127). */
const FLOW_STEPS = 24;
/** Flow fields measured slower than budgeted A* for hordes (worst tick 118 ms vs 5 ms at 500) — kept off; see D127. */
const FLOW_ENABLED = false;

/** Seconds an unreachable target is ignored by automatic target search. */
const UNREACHABLE_IGNORE = 5;

export interface TrainingJob {
  readonly unit: UnitTypeId;
  /** Seconds trained so far (only the first job in a queue advances). */
  readonly progress: number;
}

export type UnitEvent =
  | { type: 'spawned'; unit: Unit; from: number }
  | { type: 'died'; unit: Unit; by?: number }
  /** `by` = attacking unit id when known. */
  | { type: 'damaged'; unit: Unit; amount: number; by?: number }
  /** An attack was made (muzzle flash / melee swing). Ranged attacks then fire a projectile. */
  | { type: 'attacked'; attacker: Unit; target: TargetRef; at: { x: number; y: number }; projectile: number | null }
  /** A projectile landed (`hit`) or fizzled because its target was gone. */
  | { type: 'impact'; projectile: Projectile; hit: boolean }
  | { type: 'queue'; buildingId: number }
  /** A repairer restored health (for a spark effect) or had to stop (no scrap / building full). */
  | { type: 'repairing'; unit: Unit; buildingId: number; amount: number }
  | { type: 'repairStopped'; unit: Unit; buildingId: number; reason: 'done' | 'no_scrap' | 'unreachable' | 'gone' };

const EPS = 1e-9;
/** How often a unit chasing a target may re-plan (seconds). */
const CHASE_REPATH = 1;

/**
 * Every unit, training queue and projectile in a match (DECISIONS D56–D60,
 * combat D62–D66). Engine-free and deterministic: units are processed in id
 * order, pathing is deterministic A*, target choice breaks ties by id, and
 * nothing reads time except `step(dt)`.
 *
 * Simplifications (documented): units do not collide with each other and do not eat.
 */
export class UnitStore {
  private readonly units = new Map<number, MutableUnit>();
  private readonly queues = new Map<number, { unit: UnitTypeId; progress: number }[]>();
  private readonly listeners = new Set<(e: UnitEvent) => void>();
  readonly projectiles = new Projectiles();
  private nextId = 1;
  private stepCount = 0;
  /** Routes still allowed this step (Infinity outside `step`, e.g. for player commands). */
  private plansLeft = Infinity;
  /** D127: shared routes for the infected. */
  readonly flow: FlowFields;
  /** Spatial hash for target search, rebuilt every step: cell key → unit ids. */
  private cells = new Map<number, number[]>();
  private readonly unsubscribe: () => void;

  constructor(
    private readonly map: WorldMap,
    private readonly buildings: BuildingStore,
  ) {
    this.flow = new FlowFields(map.width, map.height);
    this.unsubscribe = buildings.subscribe((e) => {
      if (e.type === 'added') this.onBuildingAdded(e.building);
      else if (e.type === 'removed') {
        this.queues.delete(e.building.id);
        this.flow.invalidate();
      }
    });
  }

  get count(): number {
    return this.units.size;
  }

  get listenerCount(): number {
    return this.listeners.size;
  }

  get nextUnitId(): number {
    return this.nextId;
  }

  subscribe(l: (e: UnitEvent) => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  dispose(): void {
    this.unsubscribe();
    this.listeners.clear();
  }

  get(id: number): Unit | null {
    const u = this.units.get(id);
    return u ? snap(u) : null;
  }

  all(): Unit[] {
    return [...this.units.keys()].sort((a, b) => a - b).map((id) => snap(this.units.get(id)!));
  }

  /** Visit every unit in id order without copying (read-only view; do not keep the reference). */
  each(fn: (u: Unit) => void): void {
    for (const id of [...this.units.keys()].sort((a, b) => a - b)) fn(this.units.get(id)! as Unit);
  }

  ownedBy(owner: OwnerId): Unit[] {
    return this.all().filter((u) => u.ownerId === owner);
  }

  /** Can a unit stand on / walk through this tile? (walkable terrain and no movement-blocking building) */
  passable = (x: number, y: number): boolean => {
    if (!this.map.isWalkable(x, y)) return false;
    const b = this.buildings.occupancy.at(x, y);
    return b === 0 || !this.buildings.definition(b)?.blocksMovement;
  };

  /**
   * Passability for one owner: like `passable`, plus that owner's gates
   * (a gate blocks everyone else). Used for route planning.
   */
  passableFor(owner: OwnerId): (x: number, y: number) => boolean {
    return (x: number, y: number) => {
      if (!this.map.isWalkable(x, y)) return false;
      const id = this.buildings.occupancy.at(x, y);
      if (id === 0) return true;
      const def = this.buildings.definition(id);
      if (!def?.blocksMovement) return true;
      return !!def.ownerPassable && this.buildings.get(id)?.ownerId === owner;
    };
  }

  /**
   * Who pays for repairs (set by the Simulation): asked for `health` points on
   * `buildingId`, returns how many it could pay for (0 = none).
   */
  repairPayer: ((owner: OwnerId, buildingId: number, health: number) => number) | null = null;

  /** D128: research bonuses (set by the Simulation); 1 / 0 = none. */
  bonus: (owner: OwnerId, key: BonusKey) => number = (_o, key) => (key.endsWith('_add') ? 0 : 1);

  /** Player → team (Phase 9). Players on one team never fight each other. Unset = everyone on their own. */
  teams: ReadonlyMap<OwnerId, number> = new Map();

  /** Order repairers (units with a repair rate) to fix one of their owner's buildings. */
  repair(ids: readonly number[], buildingId: number, auto = false): void {
    for (const id of ids) {
      const u = this.units.get(id);
      if (!u || !unitDef(u.type).repairRate) continue;
      clearFight(u);
      u.order = { kind: 'repair', buildingId, ...(auto ? { auto: true } : {}) };
      u.path = [];
      u.repath = 0;
      u.repairAcc = 0;
    }
  }

  /** Live hostile units within `r` tiles of a point, nearest first (ties → lower id). For turrets. */
  enemiesNear(owner: OwnerId, x: number, y: number, r: number): Unit[] {
    const out: { u: MutableUnit; d: number }[] = [];
    for (const v of this.near(x, y, r)) {
      if (!hostile(owner, v.ownerId, this.teams)) continue;
      const d = hyp(v.x - x, v.y - y);
      if (d <= r + EPS) out.push({ u: v, d });
    }
    out.sort((a, b) => a.d - b.d || a.u.id - b.u.id);
    return out.map((o) => snap(o.u));
  }

  /** Units standing on any of these tiles (for placement checks). */
  unitsOnTiles(tiles: readonly GridPoint[]): Unit[] {
    const set = new Set(tiles.map((t) => `${t.x},${t.y}`));
    return this.all().filter((u) => set.has(`${Math.round(u.x)},${Math.round(u.y)}`));
  }

  // ---------------------------------------------------------------- training

  queue(buildingId: number): readonly TrainingJob[] {
    return (this.queues.get(buildingId) ?? []).map((j) => ({ ...j }));
  }

  enqueue(buildingId: number, unit: UnitTypeId): void {
    const q = this.queues.get(buildingId) ?? [];
    q.push({ unit, progress: 0 });
    this.queues.set(buildingId, q);
    this.emit({ type: 'queue', buildingId });
  }

  /** Remove the LAST queued job (the one furthest from finishing). */
  cancelLast(buildingId: number): TrainingJob | null {
    const q = this.queues.get(buildingId);
    const job = q?.pop() ?? null;
    if (job) this.emit({ type: 'queue', buildingId });
    return job ? { ...job } : null;
  }

  /** Create a unit next to a building (nearest open tile to its front). */
  spawn(type: UnitTypeId, ownerId: OwnerId, near: Building): Unit | null {
    const def = buildingDef(near.type);
    const front = { x: near.origin.x + def.footprint.width - 1, y: near.origin.y + def.footprint.height - 1 };
    const taken = new Set<number>();
    for (const u of this.units.values()) if (u.order.kind === 'idle') taken.add(Math.round(u.y) * this.map.width + Math.round(u.x));
    const spot = nearestOpenTiles(this.map.width, this.map.height, this.passable, { x: front.x + 1, y: front.y + 1 }, 1, taken)[0] ??
      nearestOpenTiles(this.map.width, this.map.height, this.passable, { x: front.x + 1, y: front.y + 1 }, 1)[0];
    if (!spot) return null;
    const u = this.add(type, ownerId, spot, near.id);
    // D128 Body Armour: trained soldiers come out tougher.
    const hp = this.bonus(ownerId, 'unit_hp');
    if (hp !== 1) {
      const m = this.units.get(u.id)!;
      m.maxHealth = Math.round(m.maxHealth * hp);
      m.health = m.maxHealth;
      return snap(m);
    }
    return u;
  }

  /** Direct creation (match setup, tests). Position must be passable. */
  add(type: UnitTypeId, ownerId: OwnerId, at: GridPoint, from = 0): Unit {
    if (!this.passable(at.x, at.y)) throw new Error(`Cannot place a unit at ${at.x},${at.y}`);
    const d = unitDef(type);
    const u: MutableUnit = {
      id: this.nextId++,
      type,
      ownerId,
      x: at.x,
      y: at.y,
      health: d.maxHealth,
      maxHealth: d.maxHealth,
      order: { kind: 'idle' },
      stance: 'aggressive',
      path: [],
      cooldown: 0,
      repath: 0,
      anchor: null,
      resume: null,
      threat: null,
      skip: null,
      skipFor: 0,
      repairAcc: 0,
      pendingPlan: null,
    };
    this.units.set(u.id, u);
    this.emit({ type: 'spawned', unit: snap(u), from });
    return snap(u);
  }

  // ---------------------------------------------------------------- orders (validated by the Simulation)

  /** Move a group: each unit gets its own nearby tile around the target. Player moves ignore enemies. */
  move(ids: readonly number[], target: GridPoint): void {
    this.spread(ids, target, (dest) => ({ kind: 'move', target: dest }));
  }

  /** Attack-move a group: like move, but they fight whatever they meet on the way. */
  attackMove(ids: readonly number[], target: GridPoint): void {
    this.spread(ids, target, (dest) => ({ kind: 'attackMove', target: dest }));
  }

  attack(ids: readonly number[], target: TargetRef): void {
    for (const id of ids) {
      const u = this.units.get(id);
      if (!u) continue;
      clearFight(u);
      u.order = { kind: 'attack', target };
      u.path = [];
      u.repath = 0;
    }
  }

  stop(ids: readonly number[]): void {
    for (const id of ids) {
      const u = this.units.get(id);
      if (!u) continue;
      clearFight(u);
      u.order = { kind: 'idle' };
      u.path = [];
    }
  }

  setStance(ids: readonly number[], stance: Stance): void {
    for (const id of ids) {
      const u = this.units.get(id);
      if (!u) continue;
      u.stance = stance;
      // A unit told to hold stops chasing whatever it picked for itself.
      if (stance === 'hold' && u.order.kind === 'attack' && u.order.auto) {
        clearFight(u);
        u.order = { kind: 'idle' };
        u.path = [];
      }
    }
  }

  /**
   * Deal `amount` damage to a unit as-is (already computed). It dies (is
   * removed) at zero. `by` = the attacking unit, for return fire. Returns true if it died.
   */
  damage(id: number, amount: number, by?: number): boolean {
    const u = this.units.get(id);
    if (!u || !(amount > 0)) return false;
    const dealt = Math.min(u.health, Math.round(amount));
    u.health -= dealt;
    this.emit({ type: 'damaged', unit: snap(u), amount: dealt, ...(by ? { by } : {}) });
    if (u.health > 0) {
      if (by && by !== id && this.units.has(by)) u.threat = by;
      // Shot by a building (turret, attackerId = −buildingId): answer the building.
      else if (by && by < 0 && this.buildings.get(-by)) u.threat = by;
      return false;
    }
    this.units.delete(id);
    this.emit({ type: 'died', unit: snap(u), ...(by ? { by } : {}) });
    return true;
  }

  /** Damage one hit from `attacker` would do to `target` under the combat rules (0 if either is gone). */
  hitDamage(attacker: UnitTypeId, target: TargetRef): number {
    const a = unitDef(attacker);
    return this.rawHit(a.damage, a.damageType, target);
  }

  /** Where a target is now (for range checks and visuals), or null if it no longer exists. */
  targetPoint(t: TargetRef): { x: number; y: number } | null {
    if (t.kind === 'unit') {
      const u = this.units.get(t.id);
      return u ? { x: u.x, y: u.y } : null;
    }
    const b = this.buildings.get(t.id);
    if (!b) return null;
    const fp = buildingDef(b.type).footprint;
    return { x: b.origin.x + (fp.width - 1) / 2, y: b.origin.y + (fp.height - 1) / 2 };
  }

  /** Distance from a point to a target's body (unit centre, or the building footprint's edge). */
  distanceTo(px: number, py: number, t: TargetRef): number {
    if (t.kind === 'unit') {
      const u = this.units.get(t.id);
      return u ? hyp(u.x - px, u.y - py) : Infinity;
    }
    const b = this.buildings.get(t.id);
    if (!b) return Infinity;
    const fp = buildingDef(b.type).footprint;
    const dx = Math.max(b.origin.x - 0.5 - px, 0, px - (b.origin.x + fp.width - 0.5));
    const dy = Math.max(b.origin.y - 0.5 - py, 0, py - (b.origin.y + fp.height - 0.5));
    return hyp(dx, dy);
  }

  // ---------------------------------------------------------------- simulation

  /**
   * Advance training, movement, target search, attacks and projectiles by dt seconds.
   * `onTrained` is told when a job finishes so the Simulation can spawn its unit.
   */
  step(dt: number, onTrained: (buildingId: number, unit: UnitTypeId) => void): void {
    if (!(dt > 0)) return;
    this.stepCount++;
    this.plansLeft = PLAN_BUDGET_PER_TICK;
    for (const id of [...this.queues.keys()].sort((a, b) => a - b)) {
      const q = this.queues.get(id)!;
      const job = q[0];
      if (!job) continue;
      const owner = this.buildings.get(id)?.ownerId;
      job.progress += dt * (owner ? this.bonus(owner, 'train_speed') : 1);
      if (job.progress >= unitDef(job.unit).trainSeconds - EPS) {
        q.shift();
        this.emit({ type: 'queue', buildingId: id });
        onTrained(id, job.unit);
      }
    }
    this.rebuildCells();
    for (const id of [...this.units.keys()].sort((a, b) => a - b)) {
      const u = this.units.get(id);
      if (u) this.stepUnit(u, dt);
    }
    for (const o of this.projectiles.step(dt, this)) {
      const p = o.projectile;
      if (o.hit) {
        const dealt = this.rawHit(p.damage, p.damageType, p.target);
        if (p.target.kind === 'unit') this.damage(p.target.id, dealt, p.attackerId);
        else this.buildings.damage(p.target.id, dealt);
      }
      this.emit({ type: 'impact', projectile: p, hit: o.hit });
    }
    this.plansLeft = Infinity; // player commands between steps are never delayed
  }

  snapshot(): { units: Unit[]; queues: Record<string, TrainingJob[]>; nextUnitId: number; projectiles: Projectile[] } {
    const queues: Record<string, TrainingJob[]> = {};
    for (const id of [...this.queues.keys()].sort((a, b) => a - b)) if (this.queues.get(id)!.length) queues[String(id)] = this.queue(id) as TrainingJob[];
    return { units: this.all(), queues, nextUnitId: this.nextId, projectiles: this.projectiles.all() };
  }

  // ------------------------------------------------------------------

  private spread(ids: readonly number[], target: GridPoint, order: (dest: GridPoint) => UnitOrder): void {
    const list = ids.map((id) => this.units.get(id)!).filter(Boolean);
    const spots = nearestOpenTiles(this.map.width, this.map.height, this.passable, target, list.length);
    const goal = spots.length ? spots : [target];
    // Nearest units take the nearest spots (deterministic: by distance, then id).
    list.sort((a, b) => hyp(a.x - target.x, a.y - target.y) - hyp(b.x - target.x, b.y - target.y) || a.id - b.id);
    list.forEach((u, i) => {
      const dest = goal[Math.min(i, goal.length - 1)]!;
      clearFight(u);
      u.order = order(dest);
      u.path = this.plan(u, dest);
    });
  }

  private rawHit(base: number, type: DamageType, target: TargetRef): number {
    if (target.kind === 'unit') {
      const v = this.units.get(target.id);
      if (!v) return 0;
      const d = unitDef(v.type);
      return computeDamage(base, type, d.armorClass, d.armor);
    }
    const b = this.buildings.get(target.id);
    if (!b) return 0;
    const wall = b.type === 'wall' || b.type === 'gate' ? this.bonus(b.ownerId, 'wall_armor_add') : 0;
    return computeDamage(base, type, 'structure', statsAt(b.type, b.level).armor + wall);
  }

  private stepUnit(u: MutableUnit, dt: number): void {
    const def = unitDef(u.type);
    u.cooldown = Math.max(0, u.cooldown - dt);
    u.repath = Math.max(0, u.repath - dt);
    if (u.skip) {
      u.skipFor -= dt;
      if (u.skipFor <= 0) u.skip = null;
    }

    // Target search: free units (idle, attack-moving, or busy with a building they chose) look around.
    if (this.free(u)) {
      const scan = (this.stepCount + u.id) % COMBAT.scanEveryTicks === 0;
      if (scan || u.threat !== null) {
        const t = this.acquire(u, def);
        if (t) this.engage(u, t);
        else if (scan && def.repairRate && u.order.kind === 'idle') {
          // Idle repairers fix damaged buildings of their own nearby (Phase 8).
          const b = this.nearestDamagedOwn(u, def.sight);
          if (b) this.repair([u.id], b, true);
        }
      }
    }
    u.threat = null;

    if (u.order.kind === 'repair') {
      this.stepRepair(u, def, dt);
      return;
    }

    if (u.order.kind === 'attack') {
      const t = u.order.target;
      const d = this.distanceTo(u.x, u.y, t);
      if (!Number.isFinite(d)) {
        this.disengage(u, false); // target gone
        return;
      }
      if (u.order.auto) {
        if (u.stance === 'hold' && d > def.range + EPS) {
          this.disengage(u, false);
          return;
        }
        const tp = this.targetPoint(t)!;
        if (u.anchor && hyp(tp.x - u.anchor.x, tp.y - u.anchor.y) > def.sight + (def.leash ?? COMBAT.leashTiles) + EPS) {
          this.disengage(u, true); // out of the leash: give up
          return;
        }
      }
      if (d <= def.range + EPS) {
        u.path = [];
        if (u.cooldown <= EPS) this.strike(u, def, t);
        return;
      }
      if ((u.path.length === 0 || u.repath <= 0) && this.plansLeft > 0) {
        u.path = this.plan(u, this.approachTile(u, t));
        // Staggered by id so a crowd that starts chasing together does not re-plan in the same tick.
        u.repath = CHASE_REPATH + (u.id % 5) * 0.1;
        if (u.path.length === 0) {
          // It cannot get any closer (target out of reach). Give up instead of standing there forever.
          if (u.order.auto) {
            u.skip = t;
            u.skipFor = UNREACHABLE_IGNORE;
            this.disengage(u, false);
          } else {
            clearFight(u);
            u.order = { kind: 'idle' };
          }
          return;
        }
      }
    }
    if (u.pendingPlan && (u.order.kind === 'move' || u.order.kind === 'attackMove')) {
      if (this.plansLeft <= 0) return; // wait for budget
      u.path = this.plan(u, u.pendingPlan);
      u.pendingPlan = null;
    }
    this.walk(u, def.speed * dt);
    if ((u.order.kind === 'move' || u.order.kind === 'attackMove') && u.path.length === 0 && !u.pendingPlan) {
      u.order = { kind: 'idle' };
      u.anchor = null;
    }
  }

  /** May this unit pick its own target right now? (Player move orders and player-chosen targets come first.) */
  private free(u: MutableUnit): boolean {
    const o = u.order;
    return o.kind === 'idle' || o.kind === 'attackMove' || (o.kind === 'attack' && !!o.auto && o.target.kind === 'building') || (o.kind === 'repair' && !!o.auto);
  }

  private stepRepair(u: MutableUnit, def: UnitDefinition, dt: number): void {
    const o = u.order as Extract<UnitOrder, { kind: 'repair' }>;
    const b = this.buildings.get(o.buildingId);
    const stop = (reason: 'done' | 'no_scrap' | 'unreachable' | 'gone') => {
      u.order = { kind: 'idle' };
      u.path = [];
      u.repairAcc = 0;
      this.emit({ type: 'repairStopped', unit: snap(u), buildingId: o.buildingId, reason });
    };
    if (!b || b.ownerId !== u.ownerId) return stop('gone');
    if (b.health >= b.maxHealth || b.state !== 'complete') return stop('done');
    const t: TargetRef = { kind: 'building', id: b.id };
    if (this.distanceTo(u.x, u.y, t) <= def.range + EPS) {
      u.path = [];
      u.repairAcc += (def.repairRate ?? 0) * dt;
      const whole = Math.floor(u.repairAcc + EPS);
      if (whole < 1) return;
      u.repairAcc -= whole;
      const want = Math.min(whole, b.maxHealth - b.health);
      const paid = this.repairPayer ? this.repairPayer(u.ownerId, b.id, want) : want;
      if (paid <= 0) return stop('no_scrap');
      const healed = this.buildings.repair(b.id, paid);
      this.emit({ type: 'repairing', unit: snap(u), buildingId: b.id, amount: healed });
      return;
    }
    if ((u.path.length === 0 || u.repath <= 0) && this.plansLeft > 0) {
      u.path = this.plan(u, this.approachTile(u, t));
      u.repath = CHASE_REPATH;
      if (u.path.length === 0) return stop('unreachable');
    }
    this.walk(u, def.speed * dt);
  }

  /** Nearest own complete building below max health within `r` tiles (footprint edge), ties → lower id. */
  private nearestDamagedOwn(u: MutableUnit, r: number): number | null {
    let best = 0;
    let bd = Infinity;
    const cx = Math.round(u.x);
    const cy = Math.round(u.y);
    const seen = new Set<number>();
    const R = Math.ceil(r) + 1;
    for (let y = cy - R; y <= cy + R; y++) {
      for (let x = cx - R; x <= cx + R; x++) {
        if (!this.map.inBounds(x, y)) continue;
        const id = this.buildings.occupancy.at(x, y);
        if (!id || seen.has(id)) continue;
        seen.add(id);
        const b = this.buildings.get(id);
        if (!b || b.ownerId !== u.ownerId || b.state !== 'complete' || b.health >= b.maxHealth) continue;
        const d = this.distanceTo(u.x, u.y, { kind: 'building', id });
        if (d > r + EPS) continue;
        if (d < bd - EPS || (Math.abs(d - bd) <= EPS && id < best)) (bd = d), (best = id);
      }
    }
    return best || null;
  }

  /**
   * Choose a target (DECISIONS D62): the unit that just hurt it if it may answer,
   * else the nearest enemy unit in reach, else the nearest enemy building.
   * Reach = sight (aggressive) or weapon range (hold). Ties → lower id.
   */
  private acquire(u: MutableUnit, def: UnitDefinition): TargetRef | null {
    const reach = u.stance === 'hold' ? def.range : def.sight;
    const skipped = (kind: TargetRef['kind'], id: number) => !!u.skip && u.skip.kind === kind && u.skip.id === id;
    if (u.threat !== null && u.threat < 0 && !skipped('building', -u.threat)) {
      const b = this.buildings.get(-u.threat);
      const reach = u.stance === 'hold' ? def.range : def.sight + (def.leash ?? COMBAT.leashTiles);
      if (b && hostile(u.ownerId, b.ownerId, this.teams) && this.distanceTo(u.x, u.y, { kind: 'building', id: b.id }) <= reach + EPS) return { kind: 'building', id: b.id };
    }
    if (u.threat !== null && u.threat > 0 && !skipped('unit', u.threat)) {
      const a = this.units.get(u.threat);
      if (a && hostile(u.ownerId, a.ownerId, this.teams) && hyp(a.x - u.x, a.y - u.y) <= (u.stance === 'hold' ? def.range : def.sight + (def.leash ?? COMBAT.leashTiles)) + EPS) {
        return { kind: 'unit', id: a.id };
      }
    }
    let best: MutableUnit | null = null;
    let bd = Infinity;
    for (const v of this.near(u.x, u.y, reach)) {
      if (!hostile(u.ownerId, v.ownerId, this.teams) || skipped('unit', v.id)) continue;
      const d = hyp(v.x - u.x, v.y - u.y);
      if (d > reach + EPS) continue;
      if (d < bd - EPS || (Math.abs(d - bd) <= EPS && best && v.id < best.id)) (bd = d), (best = v);
    }
    if (best) return { kind: 'unit', id: best.id };
    // Already hitting a building and no unit around: keep at it. Repairers busy repairing don't pick buildings.
    if (u.order.kind === 'attack' || u.order.kind === 'repair') return null;
    const b = this.nearestEnemyBuilding(u, reach);
    return b && skipped('building', b.id) ? null : b;
  }

  private nearestEnemyBuilding(u: MutableUnit, reach: number): TargetRef | null {
    const r = Math.ceil(reach) + 1;
    const cx = Math.round(u.x);
    const cy = Math.round(u.y);
    const seen = new Set<number>();
    let bestId = 0;
    let bd = Infinity;
    for (let y = cy - r; y <= cy + r; y++) {
      for (let x = cx - r; x <= cx + r; x++) {
        if (!this.map.inBounds(x, y)) continue;
        const id = this.buildings.occupancy.at(x, y);
        if (!id || seen.has(id)) continue;
        seen.add(id);
        const b = this.buildings.get(id);
        if (!b || !hostile(u.ownerId, b.ownerId, this.teams)) continue;
        const d = this.distanceTo(u.x, u.y, { kind: 'building', id });
        if (d > reach + EPS) continue;
        if (d < bd - EPS || (Math.abs(d - bd) <= EPS && id < bestId)) (bd = d), (bestId = id);
      }
    }
    return bestId ? { kind: 'building', id: bestId } : null;
  }

  private engage(u: MutableUnit, t: TargetRef): void {
    if (u.order.kind === 'attack' && u.order.target.kind === t.kind && u.order.target.id === t.id) return;
    if (u.order.kind === 'attackMove') u.resume = u.order.target;
    u.anchor = u.anchor ?? { x: Math.round(u.x), y: Math.round(u.y) };
    u.order = { kind: 'attack', target: t, auto: true };
    u.path = [];
    u.repath = 0;
  }

  /** The fight is over. Carry on attack-moving; aggressive units that gave up a chase walk back. */
  private disengage(u: MutableUnit, gaveUp: boolean): void {
    const wasAuto = u.order.kind === 'attack' && !!u.order.auto;
    u.path = [];
    if (wasAuto && u.resume) {
      const dest = u.resume;
      u.resume = null;
      u.anchor = null;
      u.order = { kind: 'attackMove', target: dest };
      this.route(u, dest);
      return;
    }
    if (wasAuto && gaveUp && u.anchor) {
      const home = u.anchor;
      u.anchor = null;
      u.order = { kind: 'move', target: home };
      this.route(u, home);
      return;
    }
    // Target died: stay here (anchor kept so a follow-up chase is still leashed to the same spot).
    u.order = { kind: 'idle' };
    if (!wasAuto) u.anchor = null;
  }

  private strike(u: MutableUnit, def: UnitDefinition, t: TargetRef): void {
    u.cooldown = def.cooldown;
    const at = this.targetPoint(t)!;
    if (def.projectileSpeed) {
      const p = this.projectiles.fire({ attackerId: u.id, ownerId: u.ownerId, x: u.x, y: u.y, target: t, aim: at, speed: def.projectileSpeed, damage: def.damage * this.bonus(u.ownerId, `damage_${def.damageType}`), damageType: def.damageType });
      this.emit({ type: 'attacked', attacker: snap(u), target: t, at, projectile: p.id });
      return;
    }
    this.emit({ type: 'attacked', attacker: snap(u), target: t, at, projectile: null });
    const dealt = this.rawHit(def.damage * this.bonus(u.ownerId, `damage_${def.damageType}`), def.damageType, t);
    if (t.kind === 'unit') this.damage(t.id, dealt, u.id);
    else this.buildings.damage(t.id, dealt);
  }

  private walk(u: MutableUnit, budget: number): void {
    let left = budget;
    while (left > EPS && u.path.length) {
      const next = u.path[0]!;
      const dx = next.x - u.x;
      const dy = next.y - u.y;
      const d = hyp(dx, dy);
      if (d <= left) {
        u.x = next.x;
        u.y = next.y;
        u.path.shift();
        left -= d;
      } else {
        u.x += (dx / d) * left;
        u.y += (dy / d) * left;
        left = 0;
      }
    }
  }

  // ---------------------------------------------------------------- spatial hash

  private cellKey(cx: number, cy: number): number {
    return cy * 4096 + cx;
  }

  private rebuildCells(): void {
    const cells = new Map<number, number[]>();
    const s = COMBAT.cellTiles;
    for (const id of [...this.units.keys()].sort((a, b) => a - b)) {
      const u = this.units.get(id)!;
      const k = this.cellKey(Math.floor(u.x / s), Math.floor(u.y / s));
      const list = cells.get(k);
      if (list) list.push(id);
      else cells.set(k, [id]);
    }
    this.cells = cells;
  }

  /** Live units whose cell overlaps a radius around a point (a superset; callers check distance). */
  private near(x: number, y: number, r: number): MutableUnit[] {
    const s = COMBAT.cellTiles;
    const out: MutableUnit[] = [];
    for (let cy = Math.floor((y - r) / s); cy <= Math.floor((y + r) / s); cy++) {
      for (let cx = Math.floor((x - r) / s); cx <= Math.floor((x + r) / s); cx++) {
        for (const id of this.cells.get(this.cellKey(cx, cy)) ?? []) {
          const v = this.units.get(id);
          if (v) out.push(v);
        }
      }
    }
    return out;
  }

  // ------------------------------------------------------------------

  /** Tile to walk to in order to reach a target. */
  private approachTile(u: MutableUnit, t: TargetRef): GridPoint {
    if (t.kind === 'unit') {
      const v = this.units.get(t.id)!;
      return { x: Math.round(v.x), y: Math.round(v.y) };
    }
    const b = this.buildings.get(t.id)!;
    const fp = buildingDef(b.type).footprint;
    // The open tile next to the footprint that is closest to the unit.
    let best: GridPoint | null = null;
    let bd = Infinity;
    for (let y = b.origin.y - 1; y <= b.origin.y + fp.height; y++) {
      for (let x = b.origin.x - 1; x <= b.origin.x + fp.width; x++) {
        const inside = x >= b.origin.x && x < b.origin.x + fp.width && y >= b.origin.y && y < b.origin.y + fp.height;
        if (inside || !this.passable(x, y)) continue;
        const d = hyp(x - u.x, y - u.y);
        if (d < bd) (bd = d), (best = { x, y });
      }
    }
    return best ?? { x: b.origin.x, y: b.origin.y };
  }

  /** Set a move route now, or queue it for a later tick when this step's planning budget is spent. */
  private route(u: MutableUnit, dest: GridPoint): void {
    u.path = [];
    if (this.plansLeft > 0) {
      u.path = this.plan(u, dest);
      u.pendingPlan = null;
    } else {
      u.pendingPlan = dest;
    }
  }

  /** Waypoints from the unit's current tile to `dest` (or as close as it can get). */
  private plan(u: MutableUnit, dest: GridPoint): GridPoint[] {
    const start = { x: Math.round(u.x), y: Math.round(u.y) };
    const off = Math.abs(u.x - start.x) > EPS || Math.abs(u.y - start.y) > EPS;
    // D127: the infected share flow fields (no planning budget spent); A* only when cut off from the goal.
    // Only for marching hordes (attack-move to one shared point): roaming and building attacks have
    // per-unit goals, where a field per goal would cost more than A*.
    if (FLOW_ENABLED && u.ownerId === 'infected' && u.order.kind === 'attackMove' && dest.x === u.order.target.x && dest.y === u.order.target.y) {
      const flow = this.flow.route(start, dest, this.passableFor('infected'), FLOW_STEPS);
      if (flow && flow.length) return off ? [start, ...flow] : flow;
    }
    this.plansLeft--;
    const r = findPath(this.map.width, this.map.height, this.passableFor(u.ownerId), start, dest, unitDef(u.type).pathNodes);
    // If the unit is between tiles, walking to the start tile first keeps it on the grid.
    return off ? [start, ...r.path] : r.path;
  }

  /** A new building may sit across someone's route: re-plan those routes. */
  private onBuildingAdded(b: Building): void {
    const def = buildingDef(b.type);
    if (!def.blocksMovement) return;
    this.flow.invalidate();
    const tiles = new Set(footprintTiles(b.origin, def.footprint).map((t) => `${t.x},${t.y}`));
    for (const id of [...this.units.keys()].sort((a, c) => a - c)) {
      const u = this.units.get(id)!;
      if (!u.path.some((p) => tiles.has(`${p.x},${p.y}`))) continue;
      if (u.order.kind === 'move' || u.order.kind === 'attackMove') u.path = this.plan(u, u.order.target);
      else u.path = []; // attackers re-plan on their next step
    }
  }

  private emit(e: UnitEvent): void {
    for (const l of this.listeners) l(e);
  }
}

function clearFight(u: MutableUnit): void {
  u.pendingPlan = null;
  u.anchor = null;
  u.resume = null;
  u.threat = null;
}

function snap(u: MutableUnit): Unit {
  return {
    id: u.id,
    type: u.type,
    ownerId: u.ownerId,
    x: u.x,
    y: u.y,
    health: u.health,
    maxHealth: u.maxHealth,
    order: u.order,
    stance: u.stance,
    path: u.path.map((p) => ({ ...p })),
    cooldown: u.cooldown,
  };
}
